import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  CadmusEditorContent,
  Editor,
  editorWordCount,
  useCadmusEditor,
  useEditorState,
} from "@vericus/cadmus-editor-prosemirror";
import { useManualPreset } from "@vericus/cadmus-manual";
import { colors, Text } from "@vericus/cadmus-ui";

import { useJitsu } from "@jitsu/react";

import { isUniscTenant } from "@/client/globals";
import { WorkAttachmentSection } from "@/components/work-attachment-section/work-attachment-section";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppSelector } from "@/data/hooks";
import { useReferencingFootnotesEnabledForExtendedQuestion } from "@/features/answer/hooks/use-referencing-footnotes-enabled-for-extended-question";
import {
  selectCountFootnotes,
  selectCountReferences,
} from "@/features/assignment";
import { selectAnswerSnapshot } from "@/features/authority";
import { QuestionType } from "@/generated/graphql";
import { readAnswerBlock } from "@/graphql/selectors";
import {
  EditorName,
  useEditorStoreEditor,
  useFootnoteWordCounts,
} from "@/stores/editor-store";

import * as styles from "./answer-form.css";
import { RecoveredExtendedResponseBody } from "./recovered-extended-response-body";

interface ExtendedResponseFormProps {
  /** Answer Block ID */
  answerBlockId: string;
  /** Reference Answer Block ID */
  referencesAnswerBlockId: string | null;
  /** Make the editor readonly */
  isReadonly?: boolean;
  /** Optional word limit that gets displayed with a warning when exceeded */
  wordLimit?: number;
  /** Whether attachment is enabled */
  attachmentEnabled?: boolean;
  /** Whether LDB exam mode is enabled */
  isLdbExamMode?: boolean;
}

/**
 * Answer response section to respond with rich text editor contents.
 */
export function ExtendedResponseForm(props: ExtendedResponseFormProps) {
  const {
    answerBlockId,
    referencesAnswerBlockId,
    isReadonly = false,
    wordLimit,
    attachmentEnabled,
    isLdbExamMode,
  } = props;
  const answerBlock = readAnswerBlock(answerBlockId);
  const referencingEnabled =
    useReferencingFootnotesEnabledForExtendedQuestion(answerBlockId);

  const bodyEditor = useEditorStoreEditor(answerBlockId);
  const referencesEditor = useEditorStoreEditor(referencesAnswerBlockId);
  const referencesSnapshot = useAppSelector((state) =>
    referencesAnswerBlockId != null
      ? selectAnswerSnapshot(state, referencesAnswerBlockId)
      : null
  );

  // Disabled tenants/questions still see the editor when there's existing
  // references content.
  const showReferencesEditor = referencingEnabled
    ? true
    : referencesAnswerBlockId != null && !!referencesSnapshot;

  const { track } = useJitsu();
  const onExceedLimit = useCallback(() => {
    track(JitsuEvent.ANSWER_WORD_LIMIT_EXCEEDED, {
      answer_block_id: answerBlockId,
      question_id: answerBlock?.question?.id,
      question_type: QuestionType.Extended,
    });
  }, [track, answerBlockId, answerBlock?.question?.id]);

  return (
    <>
      <div className={styles.editorResponseForm}>
        {wordLimit != null && wordLimit > 0 && (
          <div className={styles.editorWordLimit}>
            <Text kind="bodyLine" bold>
              Word limit: {wordLimit}
            </Text>
          </div>
        )}
        {attachmentEnabled === true && (
          <div className={styles.attachmentSectionWrapper}>
            <WorkAttachmentSection
              answerBlockId={answerBlockId}
              attachments={answerBlock?.blockAttachments || []}
              disabled={isReadonly}
              ldbExamMode={isLdbExamMode}
              mode={"minimal"}
            />
          </div>
        )}
        {!isReadonly && !isUniscTenant() && (
          <RecoveredExtendedResponseBody answerBlockId={answerBlockId} />
        )}
        <ExtendedResponseFormEditor
          editorName={EditorName.Body}
          answerBlockId={answerBlockId}
          editor={bodyEditor}
          isReadonly={isReadonly}
        />
        {referencesAnswerBlockId != null && showReferencesEditor && (
          <ExtendedResponseFormEditor
            editorName={EditorName.References}
            answerBlockId={referencesAnswerBlockId}
            editor={referencesEditor}
            isReadonly={isReadonly}
          />
        )}
        <WordLimit
          bodyEditor={bodyEditor}
          referencesEditor={referencesEditor}
          wordLimit={wordLimit}
          onExceedLimit={onExceedLimit}
        />
      </div>
    </>
  );
}

interface ExtendedResponseFormEditorProps {
  editorName: EditorName;
  answerBlockId: string;
  editor: Editor | null;
  isReadonly: boolean;
}

function ExtendedResponseFormEditor(props: ExtendedResponseFormEditorProps) {
  const { answerBlockId, editorName, editor, isReadonly } = props;

  const openManualPreset = useManualPreset();

  const editorContent = isReadonly ? (
    <ReadonlyEditorContent answerBlockId={answerBlockId} editor={editor} />
  ) : (
    <CadmusEditorContent
      className={styles.editorResponseEditor}
      editor={editor}
      pageMode={false}
      onOpenManualPreset={openManualPreset}
    />
  );

  return (
    <div
      className={styles.editorResponseBase}
      style={editorName === EditorName.Body ? { flexGrow: 1 } : {}}
    >
      {editorContent}
    </div>
  );
}

interface ReadonlyProps {
  answerBlockId: string;
  editor: Editor | null;
}

// Readonly version of the EditorResponseForm
function ReadonlyEditorContent(props: ReadonlyProps) {
  const { answerBlockId, editor } = props;

  const readonlyEditor = useCadmusEditor(
    {
      editorId: `${answerBlockId}-readonly`,
      content: editor?.getJSON() ?? null,
      editable: false,
      // Read-only surface, no paste risk: always render saved font marks.
      enableFontControls: true,
    },
    [answerBlockId, editor]
  );
  return (
    <CadmusEditorContent
      className={styles.editorResponseEditor}
      editor={readonlyEditor}
    />
  );
}

interface WordLimitProps {
  bodyEditor: Editor | null;
  referencesEditor: Editor | null;
  wordLimit?: number;
  onExceedLimit?: () => void;
}

function useEditorWordCount(editor: Editor | null) {
  const wordCount = useEditorState({
    editor,
    selector: (ctx) => {
      return ctx.editor ? editorWordCount(ctx.editor)?.total : undefined;
    },
  });
  return wordCount ?? 0;
}

function WordLimit({
  onExceedLimit,
  bodyEditor,
  referencesEditor,
  wordLimit,
}: WordLimitProps) {
  const includeFootnotesCountInTotal = useAppSelector(selectCountFootnotes);
  const includeReferencesCountInTotal = useAppSelector(selectCountReferences);

  const bodyEditorWordCount = useEditorWordCount(bodyEditor);
  const bodyFootnotesWordCounts = useFootnoteWordCounts(bodyEditor) ?? 0;
  const referencesEditorWordCount = useEditorWordCount(referencesEditor);

  const wordCount =
    bodyEditorWordCount +
    (includeFootnotesCountInTotal ? bodyFootnotesWordCounts : 0) +
    (includeReferencesCountInTotal ? referencesEditorWordCount : 0);

  const prevOverLimit = useRef(false);
  const limit = useMemo(
    () =>
      wordLimit !== undefined && wordLimit > 0
        ? Math.floor(wordLimit * 0.9)
        : undefined,
    [wordLimit]
  );
  const hasWordLimit = wordLimit !== undefined;
  const overlimit = wordLimit !== undefined && wordCount > wordLimit;
  const warning =
    hasWordLimit &&
    limit !== undefined &&
    wordCount > limit &&
    wordCount < wordLimit;

  useEffect(() => {
    if (overlimit && !prevOverLimit.current && onExceedLimit) {
      // Call the callback function once the word limit is exceeded
      onExceedLimit();
    }
    prevOverLimit.current = overlimit;
  }, [overlimit, onExceedLimit]);

  if (wordLimit === undefined) return null;

  // Stated in words as well as colour: the limit state was previously conveyed
  // only by turning the count amber then red, which tells a colour-blind
  // student nothing and a screen reader user nothing at all.
  const limitStatus = overlimit
    ? "Over the word limit"
    : warning
      ? "Approaching the word limit"
      : "";

  return (
    <div className={styles.editorWordCountWrapper}>
      <Text
        kind="bodyLine"
        color={overlimit ? "functionalFail" : "neutralgrey500"}
        as="span"
      >
        Word count:{" "}
        <span style={{ color: warning ? colors.amber800 : "inherit" }}>
          {wordCount}
        </span>
        {wordLimit && wordLimit > 0 && (
          <span>
            {"/"}
            {wordLimit}
          </span>
        )}
        {limitStatus !== "" && <span aria-hidden="true">{" · "}</span>}
        {/* Announced on the transition between states, not on every
            keystroke, because only the three states change it. */}
        <span role="status">{limitStatus}</span>
      </Text>
    </div>
  );
}
